import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, UserPlus, ShieldCheck, KeyRound, MailCheck, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, setToken, setUser as setStoredUser } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { toast } from "sonner";
import { COUNTRIES, countryOf, CHARGEABLE_CURRENCIES } from "../lib/countries";

const initialForm = {
  first_name: "", last_name: "", email: "", password: "",
  phone: "", billing_country: "IN", currency: "INR",
  address_line1: "", address_line2: "", city: "", state: "", postal_code: "",
  company: "",
};

export default function Signup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const { setCurrency: setCurrencyCode } = useCurrency();

  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Auto-adjust phone country prefix + currency when billing country changes.
  useEffect(() => {
    const c = countryOf(form.billing_country);
    setForm((f) => {
      // If the phone field is empty OR still starts with a previous dial code, replace it.
      const stripped = (f.phone || "").replace(/^\+\d{1,4}\s*/, "");
      const nextPhone = f.phone === "" || /^\+\d/.test(f.phone) ? `${c.dial} ${stripped}`.trim() : f.phone;
      const nextCurrency = CHARGEABLE_CURRENCIES.includes(c.currency) ? c.currency : "USD";
      return { ...f, phone: nextPhone, currency: nextCurrency };
    });
  }, [form.billing_country]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        name: `${form.first_name} ${form.last_name}`.trim(),
      };
      const { data } = await api.post("/auth/register", payload);
      if (data.verification_required) {
        setOtpToken(data.otp_token);
        setOtpSentTo(data.otp_sent_to);
        try { setCurrencyCode(form.currency); } catch {}
        toast.success(`Verification email sent to ${data.otp_sent_to}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign up failed");
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/register/verify-otp", {
        otp_token: otpToken, otp_code: otpCode,
      });
      setToken(data.token); setStoredUser(data.user); setUser(data.user);
      toast.success(`Welcome, ${data.user.first_name || data.user.name}!`);
      try {
        const stored = JSON.parse(localStorage.getItem("optivize_anon_job") || "null");
        if (stored?.id) await api.post("/ai/public/claim", { job_id: stored.id });
      } catch {}
      nav(next || "/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setVerifying(false); }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/register/resend-otp", { otp_token: otpToken });
      setOtpToken(data.otp_token);
      toast.success(`New code sent to ${data.otp_sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend code");
    }
  };

  const country = useMemo(() => countryOf(form.billing_country), [form.billing_country]);

  return (
    <div className="min-h-[80vh] flex items-start justify-center px-5 py-16">
      <div className="card p-8 max-w-3xl w-full shine-border">
        <div className="mb-6">
          <div className="tag mb-3">{otpToken ? "Verify Email" : "Create Account"}</div>
          <h1 className="text-3xl font-bold text-white" style={{fontFamily:'Outfit'}}>
            {otpToken ? "Check your inbox" : "Create your Optivize account"}
          </h1>
          {otpToken ? (
            <p className="text-slate-400 text-sm mt-2 inline-flex items-center gap-1.5">
              <MailCheck size={14}/>
              We sent a verification link + 6-digit code to <strong className="text-cyan-300">{otpSentTo}</strong>.
              Use either.
            </p>
          ) : (
            <p className="text-slate-400 text-sm mt-2">
              A verification email is sent after signup — click the link OR paste the 6-digit code below to activate your account.
            </p>
          )}
        </div>

        {!otpToken ? (
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="signup-form">
            <Field label="First name *" value={form.first_name} onChange={set("first_name")} required testid="signup-first-name" autoComplete="given-name"/>
            <Field label="Last name *"  value={form.last_name}  onChange={set("last_name")}  required testid="signup-last-name"  autoComplete="family-name"/>
            <Field label="Email *" type="email" value={form.email} onChange={set("email")} required testid="signup-email" autoComplete="email" colSpan/>
            <Field label="Password *" type="password" value={form.password} onChange={set("password")} required minLength={6} testid="signup-password" autoComplete="new-password" colSpan/>

            <div className="sm:col-span-2 grid sm:grid-cols-3 gap-4">
              <div>
                <label className="label">Billing country *</label>
                <select
                  value={form.billing_country}
                  onChange={set("billing_country")}
                  className="input w-full"
                  data-testid="signup-billing-country"
                  required
                >
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name} ({c.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Currency *</label>
                <select
                  value={form.currency}
                  onChange={set("currency")}
                  className="input w-full"
                  data-testid="signup-currency"
                  required
                >
                  {CHARGEABLE_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">Auto-set from country. Change if needed.</p>
              </div>
              <div>
                <label className="label">Phone *</label>
                <div className="flex gap-2">
                  <span className="input flex items-center px-3 text-slate-300 shrink-0" style={{minWidth:'70px'}} data-testid="signup-dial-code">
                    {country.dial}
                  </span>
                  <input
                    value={(form.phone || "").replace(new RegExp(`^\\${country.dial}\\s*`), "")}
                    onChange={(e)=>setForm(f=>({...f, phone: `${country.dial} ${e.target.value.replace(/^\+?\d{0,4}\s*/, '')}`.trim() }))}
                    className="input w-full"
                    inputMode="tel"
                    autoComplete="tel-national"
                    placeholder="Mobile number"
                    data-testid="signup-phone"
                    required
                  />
                </div>
              </div>
            </div>

            <Field label="Address line 1 *" value={form.address_line1} onChange={set("address_line1")} required testid="signup-address1" autoComplete="address-line1" colSpan/>
            <Field label="Address line 2"   value={form.address_line2} onChange={set("address_line2")}          testid="signup-address2" autoComplete="address-line2" colSpan/>

            <Field label="City *" value={form.city} onChange={set("city")} required testid="signup-city" autoComplete="address-level2"/>
            <Field label="State / Region *" value={form.state} onChange={set("state")} required testid="signup-state" autoComplete="address-level1"/>
            <Field label="Postal / ZIP *" value={form.postal_code} onChange={set("postal_code")} required testid="signup-postal" autoComplete="postal-code"/>
            <Field label="Company (optional)" value={form.company} onChange={set("company")} testid="signup-company"/>

            <div className="sm:col-span-2 mt-2">
              <button type="submit" disabled={loading}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 text-base py-3"
                data-testid="signup-submit">
                {loading ? <Loader2 size={18} className="animate-spin"/> : <UserPlus size={18}/>}
                Create account &amp; verify email
              </button>
              <p className="text-xs text-slate-500 text-center mt-3">
                By continuing you agree to our <Link to="/terms" className="text-cyan-400">Terms</Link> and <Link to="/privacy" className="text-cyan-400">Privacy Policy</Link>.
              </p>
            </div>
          </form>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5">
              <div className="text-white font-semibold flex items-center gap-2">
                <MailCheck size={16} className="text-cyan-300"/> Option 1 — Click the link in your inbox
              </div>
              <p className="text-slate-400 text-sm mt-1.5">
                One tap verification. Land right back here signed in.
              </p>
            </div>

            <div className="text-center text-slate-500 text-xs uppercase tracking-widest">or</div>

            <form onSubmit={verify} className="rounded-xl border border-white/10 bg-slate-900/40 p-5 space-y-4" data-testid="signup-verify-form">
              <div>
                <label className="label inline-flex items-center gap-1.5"><KeyRound size={12}/> Option 2 — Paste the 6-digit code</label>
                <input
                  value={otpCode}
                  onChange={e=>setOtpCode(e.target.value.replace(/\s/g,""))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="• • • • • •"
                  className="input w-full text-center text-2xl tracking-[12px] font-mono"
                  maxLength={10}
                  data-testid="signup-otp-code"
                  required
                />
              </div>
              <button type="submit" disabled={verifying}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="signup-verify-btn">
                {verifying ? <Loader2 size={16} className="animate-spin"/> : <ShieldCheck size={16}/>}
                Verify &amp; continue <ArrowRight size={14}/>
              </button>
              <div className="flex justify-between text-xs">
                <button type="button" onClick={resend}
                  className="text-cyan-400 hover:text-cyan-300" data-testid="signup-resend-btn">
                  Resend email
                </button>
                <button type="button" onClick={()=>{setOtpToken(null); setOtpCode("");}}
                  className="text-slate-400 hover:text-slate-200" data-testid="signup-edit-btn">
                  ← Use a different email
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="text-center text-sm text-gray-400 mt-6">
          Already have an account? <Link to={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-cyan-400">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, colSpan = false, type = "text", required = false, minLength, autoComplete }) {
  return (
    <div className={colSpan ? "sm:col-span-2" : ""}>
      <label className="label">{label}</label>
      <input
        value={value}
        onChange={onChange}
        type={type}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        className="input w-full"
        data-testid={testid}
      />
    </div>
  );
}
